<template>
  <div class="w-full min-h-screen flex flex-col">
    <!-- Header (logo only, to mirror the style) -->
    <HeaderBar :logo="logo">
      <template #account>
        <LoginDropdown />
      </template>
    </HeaderBar>

    <!-- Main Content -->
    <div class="flex-1 w-full">
      <div class="max-w-7xl mx-auto sm:px-6 sm:py-8">
        <!-- Card: Loading -->
        <div
          v-if="loading"
          class="bg-white/90 backdrop-blur-md sm:rounded-2xl shadow-xl border border-gray-100 p-12 flex flex-col items-center justify-center gap-4"
        >
          <img class="h-24" :src="logo" alt="Loading" />
          <p class="text-gray-600">Loading...</p>
        </div>

        <!-- Card: Error -->
        <div
          v-else-if="!loading_ok"
          class="bg-white/90 backdrop-blur-md rounded-2xl shadow-xl border border-gray-100 p-12 text-center"
        >
          <img class="h-16 mx-auto opacity-70" :src="logo" alt="" />
          <p class="mt-4 text-red-600">{{ error || "Unknown error" }}</p>
        </div>

        <!-- Card: Content -->
        <div
          v-else
          class="bg-white/90 backdrop-blur-md sm:rounded-2xl shadow-xl border border-gray-100"
        >
          <div class="p-4 sm:p-8 lg:p-12 space-y-8">
            <!-- 🆕 Page Title -->
            <header class="flex items-center justify-between">
              <h1 class="text-2xl font-semibold">Open Enodo Datasets</h1>
              <!-- optional subtitle or count -->
              <p class="text-sm text-gray-500">
                {{ filteredInvestigations.length }} result{{
                  filteredInvestigations.length === 1 ? "" : "s"
                }}
              </p>
            </header>
            <!-- Search -->
            <section class="space-y-2">
              <label for="search" class="sr-only">Search</label>
              <div class="relative">
                <input
                  id="search"
                  v-model.trim="searchQuery"
                  type="text"
                  placeholder="Search by user, title, or a word in the description…"
                  class="w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-2.5 pr-10 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  aria-label="Search investigations"
                />
                <div
                  class="pointer-events-none absolute inset-y-0 right-3 flex items-center"
                >
                  <Icon icon="mdi:magnify" class="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </section>

            <!-- List -->
            <section class="space-y-2">
              <div
                v-for="el in pagedInvestigations"
                :key="el.token"
                class="w-full"
              >
                <div
                  class="rounded-xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-shadow"
                >
                  <DatasetCard
                    :id="el.id"
                    :title="el.title || el.name"
                    :authorName="el.author_name"
                    :authorLastname="el.author_lastname"
                    :doi="el.DOI || ''"
                    :publicationDate="el.publication_date"
                    :description="el.description"
                    :authorORCID="''"
                  />
                </div>
              </div>

              <!-- Empty state -->
              <div
                v-if="filteredInvestigations.length === 0"
                class="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-5 py-8 text-center text-gray-500"
              >
                No investigations match your search.
              </div>
            </section>

            <!-- Pagination -->
            <section>
              <Pagination
                v-if="filteredInvestigations.length > 0"
                :current-page="currentPage"
                :total-pages="totalPages"
                :total-items="filteredInvestigations.length"
                :items-per-page="itemsPerPage"
                label="Bottom Pagination"
                @first="firstPage"
                @previous="previousPage"
                @page="(page) => (currentPage = page)"
                @next="nextPage"
                @last="lastPage"
              />
            </section>
          </div>
        </div>

        <!-- Footer -->
        <div class="h-10 flex flex-col items-center justify-center">
          <p class="text-xs text-gray-500">
            Powered by VUO and Data@Elettra infrastructure © 2025
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, computed, watch } from "vue";
import DatasetCard from "@/components/DatasetCard.vue";
import Pagination from "@/components/Pagination.vue";
import HeaderBar from "@/components/HeaderBar.vue";
import LoginDropdown from "@/components/LoginDropdown.vue";
import { Icon } from "@iconify/vue";
import { useLoadingService } from "@/services/LoadingService";
import { useInvestigationStore } from "@/stores/InvestigationStore";

const loadingSvc = useLoadingService();
const taskId = "dataset-list";

const investigationStore = useInvestigationStore();
const investigation_list = computed(() => investigationStore.list);
const logo = ref();
const error = computed(() => investigationStore.error);
const loading = computed(() => investigationStore.isLoading);
const hasLoaded = ref(false);
const loading_ok = computed(
  () => hasLoaded.value && investigationStore.count > 0,
);

/* ---------- Search ---------- */
const searchQuery = ref("");

/* ---------- Pagination ---------- */
const itemsPerPage = ref(5);
const currentPage = ref(1);

const normalizedQuery = computed(() => searchQuery.value.trim().toLowerCase());

// Newest first (highest id); copy so the store's list is not mutated
const sortedInvestigations = computed(() =>
  [...investigation_list.value].sort((a, b) => Number(b.id) - Number(a.id)),
);

const filteredInvestigations = computed(() => {
  if (!normalizedQuery.value) return sortedInvestigations.value;

  const q = normalizedQuery.value;
  return sortedInvestigations.value.filter((el) => {
    const DOI = String(el.doi).toLowerCase();
    const title = String(el.title || el.name || "").toLowerCase();
    const desc = String(el.description || "").toLowerCase();
    const authorName = String(el.author_name || "").toLowerCase();
    const authorLastname = String(el.author_lastname || "").toLowerCase();

    return (
      DOI.includes(q) ||
      title.includes(q) ||
      desc.includes(q) ||
      authorName.includes(q) ||
      authorLastname.includes(q)
    );
  });
});

const totalPages = computed(() =>
  Math.max(
    1,
    Math.ceil(filteredInvestigations.value.length / itemsPerPage.value),
  ),
);

watch([filteredInvestigations, itemsPerPage], () => {
  if (currentPage.value > totalPages.value)
    currentPage.value = totalPages.value;
});

watch(searchQuery, () => {
  currentPage.value = 1;
});

const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage.value);
const endIndex = computed(() =>
  Math.min(
    currentPage.value * itemsPerPage.value,
    filteredInvestigations.value.length,
  ),
);

const pagedInvestigations = computed(() =>
  filteredInvestigations.value.slice(startIndex.value, endIndex.value),
);

/* ---------- Pagination actions ---------- */
function firstPage() {
  currentPage.value = 1;
}
function previousPage() {
  if (currentPage.value > 1) currentPage.value--;
}
function nextPage() {
  if (currentPage.value < totalPages.value) currentPage.value++;
}
function lastPage() {
  currentPage.value = totalPages.value;
}

onMounted(async () => {
  loadingSvc.start(taskId, { title: "Fetching Open Data" });
  logo.value = `${process.env.BASE_URL}images/enodo-logo.png`;

  // Auto-refresh if cache is stale
  if (investigationStore.isStale) {
    try {
      await investigationStore.fetchInvestigations();
    } catch (err) {
      console.error("Failed to fetch investigations:", err);
    }
  }

  hasLoaded.value = true;
  loadingSvc.stop(taskId);
});
</script>
